import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  RefreshCw,
  X,
  Users,
  ArrowRight,
  User,
  Lightbulb,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

type NextStep = {
  id: string;
  title: string;
  rationale: string;
  action_type: string;
  priority: "high" | "medium" | "low";
};

type Person = {
  user_id: string;
  display_name: string;
  avatar_url?: string | null;
  reason: string;
};

type Recs = {
  idea_id: string;
  next_steps: NextStep[];
  people: Person[];
  dismissed_step_ids: string[];
  generated_at: string;
};

const priorityStyles: Record<string, string> = {
  high: "bg-primary/15 text-primary border-primary/30",
  medium: "bg-secondary text-foreground border-border",
  low: "bg-muted text-muted-foreground border-border",
};

const callRecs = async (
  ideaId: string,
  body: Record<string, any> = {},
): Promise<Recs> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/project-recommendations`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ idea_id: ideaId, ...body }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error ?? `Request failed (${resp.status})`);
  }
  return resp.json();
};

interface Props {
  ideaId: string;
}

const IdeaCoachTab = ({ ideaId }: Props) => {
  const qc = useQueryClient();
  const [regenerating, setRegenerating] = useState(false);

  const { data: recs, isLoading } = useQuery({
    queryKey: ["project-recommendations", ideaId],
    queryFn: () => callRecs(ideaId),
  });

  const dismissMutation = useMutation({
    mutationFn: (stepId: string) =>
      callRecs(ideaId, { action: "dismiss", dismissed_step_ids: [stepId] }),
    onMutate: async (stepId) => {
      await qc.cancelQueries({ queryKey: ["project-recommendations", ideaId] });
      const prev = qc.getQueryData<Recs>(["project-recommendations", ideaId]);
      if (prev) {
        qc.setQueryData<Recs>(["project-recommendations", ideaId], {
          ...prev,
          dismissed_step_ids: [...prev.dismissed_step_ids, stepId],
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["project-recommendations", ideaId], ctx.prev);
      toast.error("Could not dismiss step");
    },
  });

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const next = await callRecs(ideaId, { action: "regenerate" });
      qc.setQueryData(["project-recommendations", ideaId], next);
      toast.success("Coach refreshed your recommendations");
    } catch (e: any) {
      toast.error(e.message ?? "Coach failed to respond");
    } finally {
      setRegenerating(false);
    }
  };

  const visibleSteps =
    recs?.next_steps?.filter(
      (s) => !recs.dismissed_step_ids.includes(s.id),
    ) ?? [];

  return (
    <div className="space-y-6">
      {/* Recommended Next Steps */}
      <section className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm uppercase tracking-wide">
              Recommended Next Steps
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={regenerate}
            disabled={regenerating || isLoading}
            className="gap-2"
          >
            {regenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-20 rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-20 rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-20 rounded-lg bg-muted/40 animate-pulse" />
          </div>
        ) : visibleSteps.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            <Sparkles className="h-6 w-6 text-primary mx-auto mb-2" />
            No recommendations right now. Click <em>Refresh</em> for fresh ideas.
          </div>
        ) : (
          <ul className="space-y-3">
            {visibleSteps.map((step) => (
              <li
                key={step.id}
                className="group border border-border rounded-lg p-4 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase ${priorityStyles[step.priority] || ""}`}
                      >
                        {step.priority}
                      </Badge>
                      <h4 className="font-medium text-sm text-foreground">
                        {step.title}
                      </h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {step.rationale}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                    onClick={() => dismissMutation.mutate(step.id)}
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* People You Should Know */}
      <section className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm uppercase tracking-wide">
            People You Should Know
          </h3>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
          </div>
        ) : !recs?.people?.length ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No matches yet. Add more detail to your Grant-Ready Brief so the
            Coach can find relevant people.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recs.people.map((p) => (
              <Link
                key={p.user_id}
                to={`/profile/${p.user_id}`}
                className="group border border-border rounded-lg p-3 hover:border-primary/40 transition-colors flex items-start gap-3"
              >
                <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-foreground truncate">
                      {p.display_name}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                    {p.reason}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default IdeaCoachTab;